import { memo, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Loader2, Trash2 } from 'lucide-react';
import { PotentialLocation, Sighting as FirestoreSighting, FirestoreLocation } from '@/types/firestore';
import { AddSightingModal } from '@/components/sightings/AddSightingModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { getAuth } from 'firebase/auth';
import { toast } from "sonner";
import {
  selectAllBrands,
  selectAllDrinks,
  selectGlobalDataLoading,
  selectGlobalDataInitialized
} from '@/store/slices/globalDataSlice';
import { addLocation } from '@/store/slices/locationsSlice';
import { Timestamp, GeoPoint } from 'firebase/firestore';
import { useBatchWrite } from '@/hooks/firestore/operations';
import { reportSighting } from '@/lib/api';

interface PotentialLocationViewProps {
  location: PotentialLocation;
  onClose: () => void;
  onRemove: (id: string) => void;
  onUpdate: (data: Partial<PotentialLocation>) => void;
}

// Interface for local state holding potential sightings with display names
interface PotentialSightingDisplay extends Omit<FirestoreSighting, 'id' | 'createdAt' | 'firstSighted' | 'lastConfirmed' | 'confirmationCount' | 'locationId' | 'locationName' | 'drinkName' | 'userId' | 'username'> {
  brandName?: string;
  drinkName?: string;
}

/**
 * PotentialLocationView component for adding new locations to the tracker.
 * Uses RTK global state and thunks.
 */
export const PotentialLocationView = memo(({
  location,
  onClose,
  onRemove,
}: PotentialLocationViewProps) => {
  const auth = getAuth();
  const user = auth.currentUser;
  const dispatch = useAppDispatch();
  
  // Get global data from Redux store
  const brands = useAppSelector(selectAllBrands);
  const drinks = useAppSelector(selectAllDrinks);
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);
  const globalDataInitialized = useAppSelector(selectGlobalDataInitialized);
  const dataLoading = globalDataLoading === 'pending' || (globalDataLoading === 'idle' && !globalDataInitialized);
  
  // Local state management
  const [potentialSightings, setPotentialSightings] = useState<PotentialSightingDisplay[]>([]);
  const [isAddSightingOpen, setIsAddSightingOpen] = useState(false);
  const [isSubmittingPotential, setIsSubmittingPotential] = useState(false);
  const [isConfirmSubmitDialogOpen, setIsConfirmSubmitDialogOpen] = useState(false);
  
  // Use the batch write hook
  const { loading: batchLoading } = useBatchWrite();

  useEffect(() => {
    console.log('[PotentialLocationView] Rendering for', location.name);
  }, [location.name]);
  
  
  // Add sightings handler: receives minimal data, adds display names for local state
  const handleAddPotentialSightings = useCallback((sightingsToAdd: { drinkId: string; brandId: string }[]) => {
    const existingDrinkIds = new Set(potentialSightings.map(s => s.drinkId));
    const uniqueNewSightings = sightingsToAdd.filter(s => !existingDrinkIds.has(s.drinkId));
    
    const sightingsWithDetails: PotentialSightingDisplay[] = uniqueNewSightings.map(s => {
        const brand = brands.find(b => b.id === s.brandId);
        const drink = drinks.find(d => d.id === s.drinkId);
        return { 
            drinkId: s.drinkId,
            brandId: s.brandId,
            brandName: brand?.name || 'Unknown', 
            drinkName: drink?.name || undefined // Keep undefined if no name
        };
    });

    setPotentialSightings(prev => [...prev, ...sightingsWithDetails]);
  }, [potentialSightings, brands, drinks]);
  
  // Submit logic: uses RTK thunk and batch write hook
  const executeSubmit = useCallback(async () => { 
    if (!location || !location.name || !location.coordinates || !location.locationType) {
        console.error("Cannot submit potential location: Missing required fields", location);
        toast.error("Missing required location fields.");
        setIsConfirmSubmitDialogOpen(false);
        return;
    }
    setIsConfirmSubmitDialogOpen(false);
    setIsSubmittingPotential(true);

    try {
      // Convert plain coordinates object back to GeoPoint for saving
      const coordinatesGeoPoint = new GeoPoint(location.coordinates.latitude, location.coordinates.longitude);
      
      // Prepare data for FirestoreLocation (excluding fields generated by backend/DB)
      const newLocationData: Omit<FirestoreLocation, 'id' | 'createdAt' | 'updatedAt'> = {
        name: location.name,
        address: location.address || '',
        googlePlaceId: location.googlePlaceId, // Use potential location's googlePlaceId
        coordinates: coordinatesGeoPoint,
        locationType: location.locationType,
        url: location.url || '',
        averageRating: 0,
        ratingCount: 0,
        drinkCount: 0,
        lastSightingDate: Timestamp.now(), // Initialize with current timestamp
      };
      
      // Dispatch the addLocation thunk (RTK)
      const resultAction = await dispatch(addLocation(newLocationData));
      
      if (addLocation.fulfilled.match(resultAction)) {
          const newLocation = resultAction.payload; // Contains the newly created FirestoreLocation with ID
          const newLocationId = newLocation.id;
          console.log('Location added via RTK:', newLocation);

          // Ensure newLocationId is valid before proceeding with sightings
          if (!newLocationId) {
              throw new Error('Failed to get new location ID after creation.');
          }

          // Use batch API for sightings if any exist
          if (potentialSightings.length > 0) {
            // Prepare sightings payload for batch API
            const sightingsPayload = potentialSightings.map(sighting => ({
              locationId: newLocationId,
              drinkId: sighting.drinkId
            }));

            try {
              // Use reportSighting with batch payload
              console.log(`Adding ${sightingsPayload.length} sightings via API for new location ${newLocationId}`);
              const sightingResult = await reportSighting(sightingsPayload);
              console.log('Batch sightings added via API:', sightingResult);
              toast.success(`Location "${newLocation.name}" added with ${potentialSightings.length} sighting(s)!`);
            } catch (error) {
              console.error("Error adding batch sightings via API:", error);
              toast.error("Location added, but failed to add sightings: " + (error instanceof Error ? error.message : 'Unknown error'));
            }
          } else {
            toast.success(`Location "${newLocation.name}" added successfully!`);
          }

          // Make sure location.id (the temporary potential ID) exists before calling onRemove
          if (location.id) {
            onRemove(location.id); // Remove the potential marker from parent state
          }
          onClose(); // Close the info window

      } else {
          // Handle the rejected case from addLocation thunk
          const errorMessage = resultAction.payload ?? 'Failed to add location via RTK';
          throw new Error(String(errorMessage));
      }

    } catch (error) {
      console.error("Error submitting potential location:", error);
      toast.error(`Failed to add location: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmittingPotential(false);
    }
  }, [location, potentialSightings, dispatch, addLocation, onClose, onRemove]);

  // Submit handler: validate and maybe confirm
  const handleSubmit = useCallback(() => {
    if (!location.name) {
      toast.error("Location name is required.");
      return;
    }
    if (!location.locationType) {
      toast.error("Location type is required.");
      return;
    }
    if (potentialSightings.length === 0) {
      setIsConfirmSubmitDialogOpen(true); // Confirm if no sightings added
    } else {
      executeSubmit();
    }
  }, [location.name, location.locationType, potentialSightings.length, executeSubmit]);

  // --- Stable Callbacks for Modals ---
  const handleOpenAddSightingModal = useCallback(() => {
    setIsAddSightingOpen(true);
  }, []);

  const handleCloseAddSightingModal = useCallback(() => {
    setIsAddSightingOpen(false);
  }, []);
  
  return (
    <>
      {/* Location Type Selector is removed from here. It's now part of LocationHeader */}
      {/* 
      <div className="flex items-center gap-3 mb-3">
        <LocationTypeSelector
          locationType={location.locationType}
          isUpdating={false} // Potential locations don't have an "updating" state for type in this view
          isDisabled={isSubmittingPotential || batchLoading} // Disable if submitting
          onChange={handleTypeChange} // This would call the onUpdate prop
        />
      </div>
      */}
      
      {/* Potential Sightings Section */}
      <div className="border-t pt-2 mt-2">
        <div className="flex justify-between items-center mb-1">
          <h4 className="font-medium text-sm">Sightings to Add</h4>
          <Button 
            variant="outline" 
            size="sm" 
            className="h-7 text-xs" 
            onClick={handleOpenAddSightingModal}
            disabled={!user || isSubmittingPotential || batchLoading || dataLoading}
          >
            <Plus className="h-3 w-3 mr-1" /> Add Sightings
          </Button>
        </div>
        
        {/* List of potential sightings */}
        <div className="border rounded p-2 max-h-40 min-h-[4rem] overflow-y-auto">
          {potentialSightings.length > 0 ? (
            <>
              {potentialSightings.map((sighting, index) => (
                <div key={index} className="py-0.5 px-1 text-s">
                  {sighting.brandName} {sighting.drinkName && `- ${sighting.drinkName}`}
                </div>
              ))}
            </>
          ) : (
            <div className="text-s text-gray-500 text-center py-2">
              No sightings added yet. You can still submit the location.
            </div>
          )}
        </div>
      </div>
      
      {/* Info about how to add */}
      <div className="border-t pt-2 mt-2">
        <h4 className="text-sm font-medium mb-2">Add to Soda Track</h4>
        <p className="text-xs text-gray-500 mb-2">
          You can save this location to Soda Track so other users can see it.
        </p>
      </div>

      {/* Submit buttons section */} 
      <div className="flex justify-end border-t pt-2 mt-2">
        <Button 
          variant="destructive"
          size="sm" 
          className="h-7 text-xs mr-2"
          onClick={() => location.id && onRemove(location.id)}
          disabled={isSubmittingPotential}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Remove Potential Location
        </Button>
        <Button 
          size="sm" 
          className="h-7 text-xs"
          onClick={handleSubmit}
          disabled={isSubmittingPotential || !user || !location.name || !location.coordinates}
        >
          {isSubmittingPotential ? (
            <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Submitting...</>
          ) : (
            "Submit Location"
          )}
        </Button>
      </div>

      {/* Add Sighting Modal */} 
      {user && (
        <AddSightingModal 
            isOpen={isAddSightingOpen}
            onClose={handleCloseAddSightingModal}
            locationId={location.id || 'potential'}
            isPotential={true}
            onAddPotentialSightings={handleAddPotentialSightings}
        />
      )}

      {/* Confirmation Dialog */} 
      <AlertDialog open={isConfirmSubmitDialogOpen} onOpenChange={setIsConfirmSubmitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Location Without Sightings?</AlertDialogTitle>
            <AlertDialogDescription>
              You haven't added any sightings for "{location.name}". Are you sure you want to submit this location now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmittingPotential}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeSubmit} disabled={isSubmittingPotential}>
              {isSubmittingPotential ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting...</>
              ) : (
                "Yes, Submit Location"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
});

PotentialLocationView.displayName = 'PotentialLocationView'; 